import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  AlertCircle, BookOpen, Calendar, Clock, Flame, Heart,
  Moon, Music, Sparkles, Star, Sun, Sunrise, Sunset,
} from 'lucide-react';

/**
 * תבנית אריחים — כל קטגוריה בכרטיס נפרד ברשת גריד.
 * פריסה ויזואלית שונה לחלוטין מרשימה אנכית — דומה ללוח מודעות/דשבורד.
 */

const NAVY = '#1b2d44';
const GOLD = '#b8941f';
const BG = '#f5f3ef';
const CARD_BG = '#ffffff';
const BORDER = '#e8e2d6';

const CATEGORY_ICONS = {
  tachanun: AlertCircle, hallel: Music, prayerAdditions: BookOpen,
  seasonalPrayers: Sunrise, seasonalTransitions: Sun, selichot: Heart,
  weekdayPrayers: Clock, shabbatAdditions: Flame, motzaeiShabbat: Moon,
  seasonalCustoms: Star, liturgy: Sparkles, leyning: BookOpen,
  haftarah: BookOpen, mourningPeriods: Sunset, general: Calendar,
};

const CATEGORY_LABELS = {
  tachanun: 'תחנון', hallel: 'הלל', prayerAdditions: 'תוספות תפילה',
  seasonalPrayers: 'תפילות עונתיות', seasonalTransitions: 'מעברים עונתיים',
  selichot: 'סליחות', weekdayPrayers: 'תפילות ימי חול',
  shabbatAdditions: 'תוספות שבת', motzaeiShabbat: 'מוצאי שבת',
  seasonalCustoms: 'מנהגים עונתיים', liturgy: 'פיוטים וקריאות',
  leyning: 'קריאת התורה', haftarah: 'הפטרה',
  mourningPeriods: 'ימי אבל ותענית', general: 'כללי',
};

// -- צבע מבדיל לפי קטגוריה (פס עליון של הכרטיס) --------------------------
const CATEGORY_ACCENT = {
  tachanun: '#7c8fa8',
  hallel: '#c9a227',
  prayerAdditions: '#5b8a6f',
  seasonalPrayers: '#d4873a',
  seasonalTransitions: '#d4873a',
  selichot: '#8b5e6b',
  weekdayPrayers: '#6b7fa0',
  shabbatAdditions: '#8b6914',
  motzaeiShabbat: '#5a6d8a',
  seasonalCustoms: '#7a8540',
  liturgy: '#7c6aaf',
  leyning: '#5b8a6f',
  haftarah: '#5b8a6f',
  mourningPeriods: '#8b5e5e',
  general: '#7c8fa8',
};

// -- כרטיס קטגוריה בודד -------------------------------------------------------
const CategoryTile = ({ category, items }) => {
  const label = CATEGORY_LABELS[category] || category;
  const IconComp = CATEGORY_ICONS[category] || Calendar;
  const accent = CATEGORY_ACCENT[category] || '#7c8fa8';
  const hasHighPriority = items.some(it => it.priority === 'high');

  return (
    <Box sx={{
      background: CARD_BG,
      borderRadius: '8px',
      border: `1px solid ${BORDER}`,
      overflow: 'hidden',
      boxShadow: hasHighPriority
        ? `0 2px 8px rgba(185,148,31,0.15)`
        : '0 1px 3px rgba(0,0,0,0.06)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* פס צבעוני עליון + כותרת */}
      <Box sx={{
        borderTop: `3px solid ${accent}`,
        display: 'flex',
        alignItems: 'center',
        gap: 0.6,
        px: 1.25,
        py: 0.6,
        background: `${accent}08`,
      }}>
        <IconComp size={12} style={{ color: accent, flexShrink: 0 }} />
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.6rem',
          fontWeight: 700,
          color: accent,
          letterSpacing: '0.05em',
        }}>
          {label}
        </Typography>
      </Box>

      {/* פריטים */}
      <Box sx={{ px: 1.25, py: 0.5, flex: 1 }}>
        {items.map((item, i) => {
          const isHigh = item.priority === 'high';
          return (
            <Box key={item.id || `${category}-${i}`} sx={{
              py: 0.35,
              borderBottom: i < items.length - 1 ? `1px solid ${BORDER}` : 'none',
            }}>
              <Typography sx={{
                fontFamily: 'Frank Ruhl Libre, David, serif',
                fontSize: isHigh ? '0.76rem' : '0.72rem',
                fontWeight: isHigh ? 700 : 500,
                color: isHigh ? NAVY : '#3a4a5c',
                lineHeight: 1.4,
                direction: 'rtl',
              }}>
                {item.label}
                {item.value ? (
                  <Typography component="span" sx={{
                    fontFamily: 'Frank Ruhl Libre, David, serif',
                    fontSize: '0.68rem',
                    color: '#6b7a8c',
                    fontWeight: 400,
                  }}> — {item.value}</Typography>
                ) : null}
              </Typography>
              {item.detail && (
                <Typography sx={{
                  fontFamily: 'Frank Ruhl Libre, David, serif',
                  fontSize: '0.58rem',
                  color: '#94a3b8',
                  direction: 'rtl',
                  lineHeight: 1.3,
                }}>
                  {item.detail}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// -- עזר: קיבוץ לפי קטגוריה ---------------------------------------------------
function groupByCategory(items) {
  const map = {};
  items.forEach((item) => {
    const cat = item.category || 'general';
    if (!map[cat]) map[cat] = [];
    map[cat].push(item);
  });
  return Object.entries(map);
}

// -- יום lookahead — שורת כרטיסים ----------------------------------------------
const LookaheadDay = ({ dayData }) => {
  const grouped = groupByCategory(dayData.items);
  return (
    <Box sx={{ mt: 1.5, pt: 1, borderTop: `1px dashed ${BORDER}` }}>
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, David, serif',
        fontSize: '0.7rem',
        fontWeight: 700,
        color: NAVY,
        textAlign: 'center',
        mb: 0.75,
      }}>
        {dayData.hebrewDate?.hebrew || dayData.date}
      </Typography>
      {dayData.items.length === 0 ? (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.65rem',
          color: '#94a3b8',
          textAlign: 'center',
        }}>
          אין ענייני יום מיוחדים
        </Typography>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 1,
        }}>
          {grouped.map(([cat, catItems]) => (
            <CategoryTile key={cat} category={cat} items={catItems} />
          ))}
        </Box>
      )}
    </Box>
  );
};

// == תבנית אריחים ==============================================================
const Tiles = ({
  today,
  items,
  lookaheadItems,
  lookaheadDays,
  loading,
  error,
  title,
  showTitle,
}) => {
  const hebrewDateStr = today?.hebrewDate?.hebrew || '';
  const grouped = groupByCategory(items || []);

  return (
    <Box sx={{
      background: BG,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      direction: 'rtl',
      overflowY: 'auto',
      padding: '14px',
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-thumb': { background: '#d4cfc6', borderRadius: 2 },
    }}>
      {/* כותרת + תאריך */}
      <Box sx={{ textAlign: 'center', mb: 1.25 }}>
        {showTitle && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.62rem',
            color: '#8a8070',
            letterSpacing: '0.14em',
            mb: 0.15,
            fontWeight: 500,
          }}>
            {title}
          </Typography>
        )}
        {hebrewDateStr && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.9rem',
            fontWeight: 700,
            color: NAVY,
          }}>
            {hebrewDateStr}
          </Typography>
        )}
      </Box>

      {/* loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={18} sx={{ color: NAVY }} />
        </Box>
      )}

      {/* error */}
      {error && (
        <Typography sx={{ fontSize: '0.68rem', color: '#ef4444', textAlign: 'center', mb: 0.5 }}>
          שגיאה בטעינה
        </Typography>
      )}

      {/* ריק */}
      {grouped.length === 0 && !loading && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.75rem',
          color: '#94a3b8',
          textAlign: 'center',
          mt: 2,
        }}>
          אין ענייני יום מיוחדים להיום
        </Typography>
      )}

      {/* רשת כרטיסים */}
      {grouped.length > 0 && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 1,
          flex: 1,
        }}>
          {grouped.map(([cat, catItems]) => (
            <CategoryTile key={cat} category={cat} items={catItems} />
          ))}
        </Box>
      )}

      {/* ימי lookahead */}
      {lookaheadDays > 0 && lookaheadItems && lookaheadItems.length > 0 && (
        lookaheadItems.map((dayData, i) => (
          <LookaheadDay key={dayData.date || i} dayData={dayData} />
        ))
      )}
    </Box>
  );
};

export default Tiles;
